'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { ProgressBar } from '@/ui/progress-bar/ProgressBar'
import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'

export type DropZoneStatus = 'default' | 'progress' | 'error' | 'disabled'

const dropZoneTextCva = cva('text-brand-2-900', {
  variants: {
    disabled: { true: 'text-brand-2-400' },
  },
})

type DropZoneLoaderProps = {
  uploadingLabel?: string
  progressAriaLabel?: string
  progress: number
  showProgress: boolean
  isProgressFadingOut: boolean
  isDisabled: boolean
}

function DropZoneLoader({
  uploadingLabel,
  progressAriaLabel,
  progress,
  showProgress,
  isProgressFadingOut,
  isDisabled,
}: DropZoneLoaderProps) {
  return (
    <div
      className={twMerge(
        'flex w-full max-w-xs flex-col items-center justify-center gap-1 self-stretch py-1 transition-opacity duration-300',
        !showProgress && 'invisible',
        isProgressFadingOut && 'opacity-0',
      )}
      aria-hidden={!showProgress || undefined}
      {...(!showProgress && { inert: true })}
    >
      {uploadingLabel && (
        <p
          className={twMerge(
            'text-center text-md font-bold',
            dropZoneTextCva({ disabled: isDisabled }),
          )}
        >
          {uploadingLabel}
        </p>
      )}
      {progressAriaLabel && (
        <ProgressBar
          className="w-full rounded-full [&_[data-slot=indicator]]:duration-300"
          value={progress}
          aria-label={progressAriaLabel}
          data-testid="dropzone/progress"
        />
      )}
    </div>
  )
}

type DropZoneContentProps = {
  label: string
  isRequired?: boolean
  formatsHint: string
  status: DropZoneStatus
  labelId?: string
  uploadingLabel?: string
  progressAriaLabel?: string
  showProgress?: boolean
  progress?: number
  isProgressFadingOut?: boolean
  children?: ReactNode
}

export function DropZoneContent({
  label,
  isRequired,
  formatsHint,
  status,
  labelId,
  uploadingLabel,
  progressAriaLabel,
  showProgress = false,
  progress = 0,
  isProgressFadingOut = false,
  children,
}: DropZoneContentProps) {
  const isDisabled = status === 'disabled'
  const showDefault = !showProgress || isProgressFadingOut

  return (
    <div className="grid w-full justify-items-center [&>*]:col-start-1 [&>*]:row-start-1">
      <div
        className={twMerge('flex w-full flex-col items-center gap-2', !showDefault && 'invisible')}
        aria-hidden={!showDefault || undefined}
        {...(!showDefault && { inert: true })}
      >
        <div className="flex flex-col items-center justify-center gap-1">
          <Icon.UploadCloud className={dropZoneTextCva({ disabled: isDisabled })} aria-hidden />
          <div className="flex flex-col items-center gap-0">
            <span
              id={labelId}
              className={twMerge(
                'text-center font-bold leading-light',
                dropZoneTextCva({ disabled: isDisabled }),
              )}
            >
              {label}
              {isRequired && ' *'}
            </span>
            <span
              className={twMerge(
                'text-center text-sm font-medium',
                dropZoneTextCva({ disabled: isDisabled }),
              )}
            >
              {formatsHint}
            </span>
          </div>
        </div>
        {children && <div className="flex justify-center">{children}</div>}
      </div>

      <DropZoneLoader
        uploadingLabel={uploadingLabel}
        progressAriaLabel={progressAriaLabel}
        progress={progress}
        showProgress={showProgress}
        isProgressFadingOut={isProgressFadingOut}
        isDisabled={isDisabled}
      />
    </div>
  )
}

type DropZoneFieldGroupProps = WithClassName<{
  children: ReactNode
  errorId: string | false
  shouldDisplayError: boolean
  errorMessage?: string
  disabled?: boolean
}>

export function DropZoneFieldGroup({
  className,
  children,
  errorId,
  shouldDisplayError,
  errorMessage,
  disabled,
}: DropZoneFieldGroupProps) {
  return (
    <fieldset className={twMerge('flex w-full flex-col', className)} disabled={disabled}>
      {children}
      {shouldDisplayError && errorMessage && (
        <ValidationMessage id={typeof errorId === 'string' ? errorId : undefined} status="error">
          {errorMessage}
        </ValidationMessage>
      )}
    </fieldset>
  )
}
